import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Search } from "lucide-react";
import { CATEGORIES, CALCULATORS } from "@/data/calculators";
import { cn } from "@/lib/utils";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [showSearch, setShowSearch] = useState(false);
    const loc = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        setOpen(false);
        setShowSearch(false);
    }, [loc.pathname]);

    // After landing on "/" with a pending category target, scroll to it.
    // Two-step pattern handles both same-page (immediate) and cross-page (post-nav) clicks.
    const scrollToCategory = (catId) => {
        const el = document.getElementById(catId);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            return true;
        }
        return false;
    };

    const handleNavClick = (catId) => (e) => {
        e.preventDefault();
        if (loc.pathname === "/") {
            // Already home — just scroll
            scrollToCategory(catId);
        } else {
            // Navigate home, then scroll once the section mounts
            navigate("/");
            // Poll briefly for the section to exist; scroll when it does
            let tries = 0;
            const tick = () => {
                tries += 1;
                if (scrollToCategory(catId) || tries > 20) return;
                setTimeout(tick, 50);
            };
            setTimeout(tick, 50);
        }
    };

    const results = q
        ? CALCULATORS.filter((c) => c.title.toLowerCase().includes(q.toLowerCase())).slice(0, 6)
        : [];

    return (
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
            <div className="container-editorial h-16 flex items-center justify-between gap-6">
                <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group">
                    <span className="font-serif text-2xl font-semibold tracking-tight text-primary">
                        Fin<span className="text-accent">Calc</span>
                    </span>
                    <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-l border-border pl-3">
                        Straightforward money math
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {Object.values(CATEGORIES).map((c) => (
                        <a
                            key={c.id}
                            href={`/#${c.id}`}
                            onClick={handleNavClick(c.id)}
                            data-testid={`nav-${c.id}`}
                            className="px-3 py-2 text-sm text-foreground/80 hover:text-primary transition-colors cursor-pointer"
                        >
                            {c.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <button
                            aria-label="Search calculators"
                            data-testid="header-search-toggle"
                            onClick={() => setShowSearch((s) => !s)}
                            className="min-h-tap min-w-tap grid place-items-center rounded-md border border-border hover:border-primary/40 transition-colors"
                        >
                            <Search className="w-4 h-4" aria-hidden="true" />
                        </button>
                        {showSearch && (
                            <div className="absolute right-0 top-12 w-80 bg-card border border-border rounded-md shadow-lg p-2 z-50">
                                <input
                                    autoFocus
                                    placeholder="Try 'mortgage' or 'debt'…"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    data-testid="header-search-input"
                                    className="w-full h-10 px-3 rounded-md bg-background border border-border focus:outline-none focus:border-accent text-sm"
                                />
                                <ul className="mt-2 max-h-64 overflow-auto">
                                    {results.length === 0 && q && (
                                        <li className="text-xs text-muted-foreground p-2">No matches. Try a different term.</li>
                                    )}
                                    {results.map((r) => (
                                        <li key={r.slug}>
                                            <Link
                                                to={`/calculator/${r.slug}`}
                                                data-testid={`header-search-result-${r.slug}`}
                                                className="block px-3 py-2 rounded text-sm hover:bg-muted"
                                            >
                                                {r.title}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <button
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        data-testid="header-menu-toggle"
                        onClick={() => setOpen((s) => !s)}
                        className="lg:hidden min-h-tap min-w-tap grid place-items-center rounded-md border border-border"
                    >
                        {open ? <X className="w-4 h-4" aria-hidden="true" /> : <Menu className="w-4 h-4" aria-hidden="true" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className={cn("lg:hidden border-t border-border bg-background")}>
                    <div className="container-editorial py-4 grid gap-2">
                        {Object.values(CATEGORIES).map((c) => (
                            <div key={c.id}>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-3 mb-1">
                                    {c.label}
                                </div>
                                {CALCULATORS.filter((x) => x.category === c.id).map((t) => (
                                    <Link
                                        key={t.slug}
                                        to={`/calculator/${t.slug}`}
                                        data-testid={`mobile-nav-${t.slug}`}
                                        className="block py-1.5 text-sm text-foreground/80"
                                    >
                                        {t.title}
                                    </Link>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
